import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
// Components
import Button from "../components/Button";
import Tab from "../components/Tab";
import Checkbox from "../components/Checkbox";
import Form from "../components/userForms/Form";
import Participants from "../pages/Participants";
import Choices from "components/userForms/Choices";
import TermsModal from "../components/TermsModal";
import ParticipantAutoFillPopup from "components/popups/ParticipantAutoFillPopup";
import { useSnackBar } from "../components/snackBar/SnackBarContext";
import { useAutoCopyInfo } from "context/AutoCopyInfoContext";
import AutoCopyInfoBanner from "components/popups/AutoCopyInfoBanner";
import { userFieldRefs, participantFieldRefs } from "../refs/refStore";

// Api Services
import {
  submitRegisterDetails,
  updateRegisterDetails,
  getUserWithEmail,
  sendInviteLink,
} from "../api/apiService";
import { initialFormData } from "../components/userForms/initialState";
import {
  validationRules,
  participantValidationRules,
} from "../components/userForms/validationRules";
import {
  validateField,
  validateForm,
  validateFlightForm,
} from "../components/userForms/validators";
import {
  transformPayload,
  reconstructFormData,
  transformPayloadOfParticipant,
  reconstructFormDataOfParticipant,
} from "../components/userForms/transformPayload";
// Assets
import Info from "../assets/icons/ic_info.svg";
import { IoIosAddCircleOutline } from "react-icons/io";
import sectionFieldMap from "../utils/sectionFieldMap";

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackBar();
  const topRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const submitRef = useRef(null);
  const { parentAutoCopiedInfo, setParentAutoCopiedInfo } = useAutoCopyInfo();
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeView, setActiveView] = useState("my-info"); // toggle state
  const [participantTabs, setParticipantTabs] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [allParticipantsData, setAllParticipantsData] = useState([]);
  const [participantErrors, setParticipantErrors] = useState([]);
  const [isSubmitClicked, setSubmitClicked] = useState(false);
  const [isDeleteClicked, setDeleteClicked] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [agreeError, setAgreeError] = useState("");
  const [insuranceConsentError, setInsuranceConsentError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [email, setEmail] = useState("");
  const [isNewUser, setNewUser] = useState(false);
  const [isLoggedIn, setLoggedIn] = useState(false);
  // user
  const [outboundFlights, setOutboundFlights] = useState([]);
  const [inboundFlights, setInboundFlights] = useState([]);
  // participants
  const [participantOutboundFlights, setParticipantOutboundFlights] = useState(
    [],
  );
  const [participantInboundFlights, setParticipantInboundFlights] = useState(
    [],
  );
  const [chooseAddParticipant, setChooseAddParticipant] = useState("");
  const [showChoices, setShowChoices] = useState(false);
  const [tripName, setTripName] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [isInvited, setIsInvited] = useState();
  const [categoryValue, setCategoryValue] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [participantAutoFill, setParticipantAutoFill] = useState(false);
  const [modifiedUpdateStatus, setModifiedUpdateStatus] = useState(false);
  const [autoFillOptions, setAutoFillOptions] = useState({
    address: true,
    emergencyContact: true,
    airTravel: true,
  });
  const [autoCopiedInfo, setAutoCopiedInfo] = useState(false);
  const [isUpdateCopyFlow, setIsUpdateCopyFlow] = useState(false);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [editedField, setEditedField] = useState(null);
  const [editedScope, setEditedScope] = useState("");
  const [inviteFormData, setInviteFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    tripName: sessionStorage.getItem("tripName"),
    senderName: formData.firstName,
    senderEmailId: formData.email,
  });

  useEffect(() => {
    const trip = sessionStorage.getItem("tripName");
    setTripName(trip);
  }, []);

  useEffect(() => {
    setInviteFormData({
      firstName: "",
      lastName: "",
      email: "",
      tripName: sessionStorage.getItem("tripName"),
      senderName: formData.firstName,
      senderEmailId: formData.email,
    });
  }, [formData.firstName, formData.email]);

  useEffect(() => {
    const userType = localStorage.getItem("userType");
    if (userType && userType !== "undefined") setShowPopup(false);
    else setShowPopup(true);
  }, []);

  useEffect(() => {
    if (activeView === "my-info" && topRef.current) {
      // Small delay to ensure rendering
      setTimeout(() => {
        scrollContainerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [activeView]);

  useEffect(() => {
    if (location.state?.infoMessage) {
      setAutoCopiedInfo(location.state.infoMessage);
    }
  }, [location.state]);

  const handleChange = (e) => {
    // Detect real user interaction
    if (e.isTrusted || e.fromUser) {
      setHasUserEdited(true);
      setAutoSaveEnabled(true); // enable autosave ONLY after first real edit
      setEditedField(e.target.name); // 🔥 track which field changed
      setEditedScope("user");
    }

    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const error = validateField(name, value, validationRules(formData));
    if (error != null) setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const sectionRefs = {
    section1: useRef(null),
    section2: useRef(null),
    section3: useRef(null),
    section4: useRef(null),
    section5: useRef(null),
    section6: useRef(null),
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getUserWithEmail(email, tripName);
        setIsInvited(result.personalInfoDTO.childInvite);
        if (
          result.personalInfoDTO.childInvite === "yes" ||
          result.personalInfoDTO.childInvite === "no"
        )
          setShowPopup(false);
        const isInvited = result.personalInfoDTO.childInvite;
        if (isInvited === "yes" || isInvited === "no") {
          localStorage.setItem("userType", isInvited);
          localStorage.setItem("isInvited", isInvited);
          handleChange({ target: { name: "userType", value: isInvited } });
        }

        const reconstructedFormData = reconstructFormData(result);
        setFormData(reconstructedFormData);
        setIsInitialLoadDone(true);
        setHasUserEdited(false); // 🛑 no auto-save until user manually edits
        setAutoSaveEnabled(false); // ⛔ stop autosave until user interacts

        setChooseAddParticipant(
          reconstructedFormData.participants.length === 0 ? "no" : "yes",
        );
        let index = 0;
        for (const participant of reconstructedFormData.participants) {
          const newParticipant = reconstructFormDataOfParticipant(participant);
          setAllParticipantsData((prev) => [
            ...prev,
            {
              id: Date.now(),
              formData: newParticipant,
            },
          ]);

          setParticipantOutboundFlights(newParticipant.outboundFlights);
          setParticipantInboundFlights(newParticipant.inboundFlights);

          const newTabId = `participant-${index + 1}`;
          setParticipantTabs((prev) => [...prev, newTabId]);
          index++;
        }
        setFormErrors({});
        setParticipantErrors([]);
        setOutboundFlights(reconstructedFormData.outboundFlights);
        setInboundFlights(reconstructedFormData.inboundFlights);
      } catch (err) {
        console.error(err);
      }
    };

    if (!isNewUser && isLoggedIn && email) {
      fetchData();
    } else {
      setFormData({ email: email });
      setIsInvited("");
      setAllParticipantsData([]);
      setInboundFlights([]);
      setOutboundFlights([]);
      setParticipantOutboundFlights([]);
      setParticipantInboundFlights([]);
      setParticipantTabs([]);
    }
  }, [isNewUser, isLoggedIn, email, tripName]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const email = localStorage.getItem("email");
      setEmail(email);
      const isNewUser =
        localStorage.getItem("isNewUser")?.toLowerCase() === "true";
      const isLoggedIn =
        localStorage.getItem("isLoggedIn")?.toLowerCase() === "true";
      if (isLoggedIn) setLoggedIn(isLoggedIn);
      if (isNewUser) setNewUser(isNewUser);
    }
  });

  useEffect(() => {
    if (isDeleteClicked || isSubmitClicked) handleSubmit();
  }, [isSubmitClicked, allParticipantsData]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    const handleScroll = () => {
      const sectionIds = Object.keys(sectionRefs);
      let minDistance = Number.POSITIVE_INFINITY;

      sectionIds.forEach((id) => {
        const el = sectionRefs[id].current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const offsetTop =
            rect.top - scrollContainer.getBoundingClientRect().top;

          const distance = Math.abs(offsetTop - 60); // Adjust for header height
          if (distance < minDistance) {
            minDistance = distance;
          }
        }
      });
    };

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });

      setTimeout(() => {
        handleScroll(); // trigger once after load
      }, 200);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  });

  const handleAddParticipantTab = () => {
    // Run validation first
    const { isValid, errors } = validateForm(
      formData,
      validationRules(formData),
    );

    // Update formErrors state immediately
    setFormErrors(errors);

    // If invalid, scroll to the first field with error
    if (!isValid) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const fieldElement = document.querySelector(
          `[name="${firstErrorField}"]`,
        );

        if (fieldElement) {
          // If element is inside a custom wrapper (like <label> or <div>), scroll that instead
          const scrollTarget = fieldElement.closest("label") || fieldElement;

          scrollTarget.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // ✅ Skip focus for checkboxes and radios (avoid console warning)
          if (
            fieldElement.type !== "checkbox" &&
            fieldElement.type !== "radio"
          ) {
            fieldElement.focus({ preventScroll: true });
          }
        }
      }
      showSnackbar("error", "Please complete all mandatory fields.");
      return; // stop execution
    }

    if (!runValidation(false)) {
      setChooseAddParticipant("noo");
      return;
    }

    // if (formData.Category === "Double" && !formData.isKnownUser) {
    //   setFormErrors((prev) => ({
    //     ...prev,
    //     isKnownUser: "This field is required",
    //   }));
    //   showSnackbar(
    //     "error",
    //     "Please complete all mandatory fields in the primary traveler section."
    //   );
    //   return;
    // }

    // if (
    //   showPopup &&
    //   (formData.userType === undefined || formData.userType === "")
    // ) {
    //   setFormErrors((prev) => ({
    //     ...prev,
    //     userType: "This field is required",
    //   }));
    //   showSnackbar(
    //     "error",
    //     "Please complete all mandatory fields in the primary traveler section."
    //   );
    //   return;
    // }

    if (
      showPopup &&
      formData.userType === "yes" &&
      (formData.isVerified === undefined || !formData.isVerified)
    ) {
      setFormErrors((prev) => ({
        ...prev,
        userType: "It is mandatory to verify the email if yes selected",
      }));
      showSnackbar(
        "error",
        "Please complete all mandatory fields in the primary traveler section.",
      );
      return;
    }
    setParticipantAutoFill(true); // show popup first

    // const newId = participantTabs.length + 1;
    // const newTabId = `participant-${newId}`;
    // setParticipantTabs((prev) => [...prev, newTabId]);
    // setActiveView(newTabId);
    // setActiveIndex(newId);
    // setAllParticipantsData((prev) => [
    //   ...prev,
    //   {
    //     id: Date.now(),
    //     formData: {},
    //   },
    // ]);
  };

  const confirmAddParticipant = () => {
    setModifiedUpdateStatus(true);
    // -------------------------
    // UPDATE FLOW (NO new tabs)
    // -------------------------
    if (isUpdateCopyFlow) {
      if (autoFillOptions.address) {
        setAllParticipantsData((prev) =>
          prev.map((participant) => ({
            ...participant,
            formData: {
              ...participant.formData,
              address: formData.address,
              city: formData.city,
              state: formData.state,
              zip: formData.zip,
              country: formData.country,
            },
          })),
        );
      }

      if (autoFillOptions.airTravel) {
        const participantOutFlights = outboundFlights.map(
          ({ flightId, ...rest }) => ({
            ...rest,
          }),
        );

        const participantInFlights = inboundFlights.map(
          ({ flightId, ...rest }) => ({
            ...rest,
          }),
        );

        setParticipantOutboundFlights(participantOutFlights);
        setParticipantInboundFlights(participantInFlights);
      }

      // Go to the first participant tab
      setActiveView(participantTabs[0]);
      setActiveIndex(1);

      // Close popup
      setParticipantAutoFill(false);

      // Disable update-copy mode
      setIsUpdateCopyFlow(false);

      return; // Stop here → do NOT submit
    }

    // -------------------------
    // POST FLOW (Create new)
    // -------------------------
    const newId = participantTabs.length + 1;
    const newTabId = `participant-${newId}`;

    // Create participant data
    let newParticipantData = {};

    if (autoFillOptions.address) {
      newParticipantData.address = formData.address;
      newParticipantData.city = formData.city;
      newParticipantData.state = formData.state;
      newParticipantData.zip = formData.zip;
      newParticipantData.country = formData.country;
    }

    const participantOutFlights = outboundFlights.map(
      ({ flightId, ...rest }) => ({
        ...rest,
      }),
    );

    const participantInFlights = inboundFlights.map(
      ({ flightId, ...rest }) => ({
        ...rest,
      }),
    );
    if (autoFillOptions.airTravel) {
      setParticipantOutboundFlights(participantOutFlights);
      setParticipantInboundFlights(participantInFlights);
    }

    setParticipantTabs((prev) => [...prev, newTabId]);
    setActiveView(newTabId);
    setActiveIndex(newId);

    setAllParticipantsData((prev) => [
      ...prev,
      {
        id: Date.now(),
        formData: newParticipantData,
      },
    ]);

    setParticipantAutoFill(false); // close popup
  };

  const handleRemoveParticipantTab = async (tabIdToRemove, indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      Category: "Single",
    }));
    setFormData((prev) => ({
      ...prev,
      isKnownUser: "",
    }));
    setCategoryValue("Single"); // ADD THIS LINE
    setChooseAddParticipant("no");

    setParticipantTabs((prevTabs) => {
      const updated = prevTabs.filter((_, index) => index !== indexToRemove);
      // Set activeView to previous participant tab if exists, else my-info
      if (updated.length > 0) {
        const fallbackIndex = indexToRemove > 0 ? indexToRemove - 1 : 0;
        setActiveView(updated[fallbackIndex]);
        setActiveIndex(fallbackIndex + 1);
      } else {
        setActiveView("my-info");
        setActiveIndex(-1);
      }
      return updated;
    });

    setAllParticipantsData((prev) => {
      const updated = prev.filter((_, index) => index !== indexToRemove);
      return updated;
    });
    setParticipantInboundFlights([]);
    setParticipantInboundFlights([]);
    setDeleteClicked(true);
    setSubmitClicked(true);
  };

  const handleParticipantChange = (
    index,
    updatedParticipant,
    fromUser = false,
    e,
  ) => {
    if (fromUser) {
      setEditedField(e.target.name); // optional, keep tracking
      setHasUserEdited(true); // Only mark for real user edit
      setEditedScope("participant");
    }
    setAllParticipantsData((prevData) => {
      const newData = [...prevData];
      newData[index] = updatedParticipant;
      return newData;
    });
  };

  const runValidation = (shouldValidateChoices) => {
    let { isValid, errors } = validateForm(formData, validationRules(formData));
    const inBoundErrors = validateFlightForm(inboundFlights);
    const outBoundErrors = validateFlightForm(outboundFlights);

    setFormErrors({
      ...errors,
      inboundFlights: inBoundErrors.errors,
      outboundFlights: outBoundErrors.errors,
    });
    let isParticipantError = false;
    const newParticipantErrors = [];

    allParticipantsData.forEach((participant, index) => {
      const participantError = validateForm(
        participant.formData,
        participantValidationRules(participant.formData),
      );

      // Assign directly at the index instead of push
      newParticipantErrors[index] = participantError.errors;

      if (!participantError.isValid) {
        setActiveView(`participant-${index + 1}`);
        isParticipantError = true;

        const firstErrorField = Object.keys(participantError.errors)[0];
        if (firstErrorField) {
          const fieldElement = document.querySelectorAll(
            `[name="${firstErrorField}"]`,
          )[index + 1];
          if (fieldElement) {
            // If element is inside a custom wrapper (like <label> or <div>), scroll that instead
            const scrollTarget = fieldElement.closest("label") || fieldElement;
            setTimeout(() => {
              scrollTarget.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }, 500); //

            // ✅ Skip focus for checkboxes and radios (avoid console warning)
            if (
              fieldElement.type !== "checkbox" &&
              fieldElement.type !== "radio"
            ) {
              fieldElement.focus({ preventScroll: true });
            }
          }
        }
        showSnackbar("error", "Please complete all mandatory fields.");
        return; // stop execution
      }
    });

    // Set all errors at once, with index alignment
    setParticipantErrors(newParticipantErrors);
    if (
      !isValid ||
      !inBoundErrors.isValid ||
      !outBoundErrors.isValid ||
      isParticipantError
    ) {
      showSnackbar(
        "error",
        (!isValid || !inBoundErrors.isValid || !outBoundErrors.isValid) &&
          isParticipantError
          ? "Please complete all mandatory fields in both the primary and second traveler sections."
          : isParticipantError
            ? "Please complete all mandatory fields in the second traveler section."
            : "Please complete all mandatory fields in the primary traveler section.",
      );

      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setDeleteClicked(false);
    setSubmitClicked(false);
    // Run validation first
    const { isValid, errors } = validateForm(
      formData,
      validationRules(formData),
    );

    // Update formErrors state immediately
    setFormErrors(errors);

    // If invalid, scroll to the first field with error
    if (!isValid) {
      setActiveView("my-info");
      setActiveIndex(-1);

      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const fieldElement = document.querySelector(
          `[name="${firstErrorField}"]`,
        );

        if (fieldElement) {
          // If element is inside a custom wrapper (like <label> or <div>), scroll that instead

          setTimeout(() => {
            const field = document.querySelector(`[name="${firstErrorField}"]`);
            field?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100); //

          // ✅ Skip focus for checkboxes and radios (avoid console warning)
          if (
            fieldElement.type !== "checkbox" &&
            fieldElement.type !== "radio"
          ) {
            fieldElement.focus({ preventScroll: true });
          }
        }
      }
      showSnackbar("error", "Please complete all mandatory fields.");
      return; // stop execution
    }

    // Continue your other validations...
    if (!runValidation(true)) return;

    const isDuplicate = allParticipantsData.some(
      (participant) => formData.email === participant.formData.email,
    );

    if (isDuplicate) {
      showSnackbar(
        "error",
        "Secondary traveler email is same as Primary traveler",
      );
      return;
    }

    // if (
    //   showPopup &&
    //   (formData.userType === undefined || formData.userType === "")
    // ) {
    //   setFormErrors((prev) => ({
    //     ...prev,
    //     userType: "This field is required",
    //   }));
    //   showSnackbar(
    //     "error",
    //     "Please complete all mandatory fields in the primary traveler section."
    //   );
    //   return;
    // }

    if (formData.Category === "Double" && !formData.isKnownUser) {
      setFormErrors((prev) => ({
        ...prev,
        isKnownUser: "This field is required",
      }));
      showSnackbar(
        "error",
        "Please complete all mandatory fields in the primary traveler section.",
      );
      return;
    }

    if (
      showPopup &&
      formData.userType === "yes" &&
      (formData.isVerified === undefined || !formData.isVerified)
    ) {
      setFormErrors((prev) => ({
        ...prev,
        userType: "It is mandatory to verify the email if yes selected",
      }));
      showSnackbar(
        "error",
        "Please complete all mandatory fields in the primary traveler section.",
      );
      return;
    }

    if (!formData.insuranceConsent) {
      setInsuranceConsentError(
        "You must confirm your responsibility for travel insurance.",
      );
      return;
    }

    if (!formData.termsConditions) {
      setAgreeError("You must agree to the Terms and Conditions.");
      return;
    }

    const files = [];
    const payload = transformPayload(
      formData,
      outboundFlights,
      inboundFlights,
      tripName,
    );
    console.log(payload, "Transform Payload");

    const renameFile = (originalFile, newName) => {
      if (!originalFile) {
        console.error("renameFile: originalFile is undefined");
        return null;
      }
      return new File([originalFile], newName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
      });
    };

    files.push(
      renameFile(
        payload.file,
        `${payload.personalInfoDTO.lastName}_${payload.personalInfoDTO.firstName}.png`,
      ),
    );

    payload.participants = [];

    for (const participant of allParticipantsData) {
      participant.formData.isKnownUser = formData.isKnownUser;
      const participantData = transformPayloadOfParticipant(
        participant.formData,
        participantOutboundFlights,
        participantInboundFlights,
      );
      payload.participants.push(participantData);
      files.push(
        renameFile(
          participantData.file,
          `${participantData.personalInfoDTO.lastName}_${participantData.personalInfoDTO.firstName}.png`,
        ),
      );
    }
    console.log(payload, "post Payload");

    try {
      const response = !isNewUser
        ? await updateRegisterDetails(
            payload,
            tripName,
            payload.personalInfoDTO.personalId,
            files,
          )
        : await submitRegisterDetails(payload, tripName, files);
      console.log(
        inviteFormData.firstName,
        inviteFormData.email,
        formData.isKnownUser,
        "invite link send",
      );

      if (
        inviteFormData.firstName &&
        inviteFormData.email &&
        formData.isKnownUser === "no"
      ) {
        await sendInviteLink(inviteFormData, tripName);
      }

      if (!isDeleteClicked) {
        showSnackbar(
          "success",
          `${!isNewUser ? "Updated" : "Submitted"} successfully!`,
        );
        console.log("Server Response:", response);
        localStorage.clear();
        navigate("/logout");
      } else {
        showSnackbar("success", `Participant Removed successfully!`);
      }
    } catch (error) {
      console.error("Submit Error:", error);
      showSnackbar("error", error.message);
    }
  };

  const handleSaveAsDraft = async () => {
    const files = [];
    const payload = transformPayload(
      formData,
      outboundFlights,
      inboundFlights,
      tripName,
    );
    payload.personalInfoDTO.status = "DRAFT";
    console.log(payload, "Draft Payload");

    const renameFile = (originalFile, newName) => {
      if (!originalFile) {
        console.error("renameFile: originalFile is undefined");
        return null;
      }
      return new File([originalFile], newName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
      });
    };

    files.push(
      renameFile(
        payload.file,
        `${payload.personalInfoDTO.lastName}_${payload.personalInfoDTO.firstName}.png`,
      ),
    );

    payload.participants = [];

    for (const participant of allParticipantsData) {
      participant.formData.isKnownUser = formData.isKnownUser;
      const participantData = transformPayloadOfParticipant(
        participant.formData,
        participantOutboundFlights,
        participantInboundFlights,
      );
      payload.participants.push(participantData);
      files.push(
        renameFile(
          participantData.file,
          `${participantData.personalInfoDTO.lastName}_${participantData.personalInfoDTO.firstName}.png`,
        ),
      );
    }

    try {
      const response = !isNewUser
        ? await updateRegisterDetails(
            payload,
            tripName,
            payload.personalInfoDTO.personalId,
            files,
          )
        : await submitRegisterDetails(payload, tripName, files);
      // ✅ Set isNewUser = false in localStorage
      localStorage.setItem("isNewUser", "false");
      setNewUser(false); // also update your state immediately

      const result = await getUserWithEmail(email, tripName);
      setIsInvited(result.personalInfoDTO.childInvite);
      if (
        result.personalInfoDTO.childInvite === "yes" ||
        result.personalInfoDTO.childInvite === "no"
      )
        setShowPopup(false);
      const isInvited = result.personalInfoDTO.childInvite;
      if (isInvited === "yes" || isInvited === "no") {
        localStorage.setItem("userType", isInvited);
        localStorage.setItem("isInvited", isInvited);
        handleChange({ target: { name: "userType", value: isInvited } });
      }

      const reconstructedFormData = reconstructFormData(result);
      setFormData(reconstructedFormData);
      setIsInitialLoadDone(true);
      setHasUserEdited(false); // 🛑 no auto-save until user manually edits
      setAutoSaveEnabled(false); // ⛔ stop autosave until user interacts

      if (response) {
        showSnackbar("success", "Form saved as draft successfully!");
      } else {
        showSnackbar("error", "Failed to save draft. Please try again.");
      }
    } catch (error) {
      console.error("Submit Error:", error);
      showSnackbar("error", error.message);
    }
  };

  useEffect(() => {
    if (participantTabs.length > 0) {
      setShowSubmit(true);
    }
  }, [participantTabs]);

  useEffect(() => {
    const shouldShowChoices = () => {
      if (activeIndex === allParticipantsData.length) return true;
      if (formData.isKnownUser === "yes") return false;
      return true;
    };

    setShowChoices(shouldShowChoices);
  }, [
    formData.isKnownUser,
    allParticipantsData,
    activeIndex,
    chooseAddParticipant,
    activeView,
  ]);

  const handleCategoryChangeFromChild = (categoryValue) => {
    setCategoryValue(categoryValue); // update local state
  };

  useEffect(() => {
    if (categoryValue === "Single") {
      setShowChoices(true);
    } else if (
      categoryValue === "Double" &&
      (formData.isKnownUser === "no" || !formData.isKnownUser)
    ) {
      setShowChoices(true);
    } else if (formData.userType === "yes") {
      setShowChoices(true);
    } else if (formData.userType === "no" && !categoryValue) {
      setShowChoices(true);
    } else {
      setShowChoices(false);
    }
  }, [formData.userType, categoryValue, formData.isKnownUser]);

  const handleAgreeTerms = () => {
    handleChange({ target: { name: "termsConditions", value: true } });
    setAgreeError("");
    setShowTermsModal(false);
  };

  const handleDeclineTerms = () => {
    handleChange({ target: { name: "termsConditions", value: false } });
    setShowTermsModal(false);
  };

  useEffect(() => {
    if (!editedField || !editedScope) return;

    let input = null;

    if (editedScope === "user") {
      input = userFieldRefs[editedField]?.current;
    }

    if (editedScope === "participant") {
      input = participantFieldRefs[editedField]?.current;
    }

    if (!input) return;

    const value =
      input.type === "checkbox" ? input.checked : input.value?.trim();

    if (value) {
      setActiveCount((prev) => prev + 1);
      console.log(value, "input count");
    }
  }, [editedField, editedScope]);

  // useEffect(() => {
  //   if (!isInitialLoadDone) return; // ⛔ block before API load finishes
  //   if (!autoSaveEnabled) return; // ⛔ block until first USER edit
  //   if (!hasUserEdited) return; // ⛔ block on refresh/reconstruct

  //   console.log(activeCount, "activeCount");
  //   if (activeCount > 5 && formData.isKnownUser !== "no") {
  //     console.log("Auto-Saving draft...");
  //     // handleSaveAsDraft();
  //   }
  // }, [activeCount]);

  const onSectionExit = (sectionId) => {
    if (!isInitialLoadDone) return;
    if (!autoSaveEnabled) return;
    if (!hasUserEdited) return;

    const fields = sectionFieldMap[sectionId];
    if (!fields) return;

    // Check if user filled ANY field in this section
    const isAnyFieldFilled = fields.some((field) => {
      const value = formData[field];
      return value !== undefined && value !== null && value !== "";
    });

    if (isAnyFieldFilled) {
      console.log(`Auto-saving Draft - User edited something in ${sectionId}`);
      // handleSaveAsDraft();
    }
  };

  return (
    <div className="">
      <div className="bg-custom-image bg-cover bg-center h-[100dvh] w-full">
        <div className="pb-4 h-[100dvh] md:h-screen mx-auto bg-white bg-opacity-95 scrollbar-thin overflow-y-auto">
          <div className="block sm:flex h-full">
            <div className="h-full max-w-screen-md mx-auto">
              <div className="border-r border-l border-inputBorder">
                <div className="sticky top-0 z-20 bg-white border-b shadow-sm">
                  <Tab
                    activeView={activeView}
                    onToggle={setActiveView}
                    setActiveIndex={setActiveIndex}
                    onAdd={handleAddParticipantTab}
                    participantTabs={participantTabs}
                    setLoggedIn={setLoggedIn}
                    handleSaveAsDraft={handleSaveAsDraft}
                    formData={formData}
                  />

                  {/* For primary user */}
                  <AutoCopyInfoBanner
                    message={autoCopiedInfo}
                    show={activeView === "my-info" && !!autoCopiedInfo}
                    onClose={() => setAutoCopiedInfo("")}
                  />

                  {/* For participants */}
                  <AutoCopyInfoBanner
                    message={parentAutoCopiedInfo}
                    show={activeView !== "my-info" && !!parentAutoCopiedInfo}
                    onClose={() => setParentAutoCopiedInfo("")}
                  />
                </div>

                <div
                  ref={scrollContainerRef}
                  className="overflow-y-auto border-b h-full max-h-[90%] bg-white bg-opacity-0"
                >
                  <div
                    ref={submitRef}
                    className={`${
                      activeView === "my-info" ? "block" : "hidden"
                    }`}
                  >
                    <Form
                      showPopup={showPopup}
                      sectionRefs={sectionRefs}
                      handleAddParticipantTab={handleAddParticipantTab}
                      handleChange={handleChange}
                      formErrors={formErrors}
                      setOutboundFlights={setOutboundFlights}
                      setInboundFlights={setInboundFlights}
                      outboundFlights={outboundFlights}
                      inboundFlights={inboundFlights}
                      formData={formData}
                      onChoiceChange={(selected) => {
                        setFormData((prev) => ({
                          ...prev,
                          travelerChoice: selected,
                        }));
                      }}
                      setFormErrors={setFormErrors}
                      isInvited={isInvited}
                      onKnownUserChange={(value) => {
                        setFormData((prev) => ({
                          ...prev,
                          isKnownUser: value,
                        }));
                      }} // ✅ set state
                      onCategoryChange={handleCategoryChangeFromChild} // ✅ passed here
                      allParticipantsData={allParticipantsData}
                      setParticipantOutboundFlights={
                        setParticipantOutboundFlights
                      }
                      setParticipantInboundFlights={
                        setParticipantInboundFlights
                      }
                      fieldRefs={userFieldRefs}
                      inviteFormData={inviteFormData}
                      setInviteFormData={setInviteFormData}
                      onSectionExit={onSectionExit}
                    />

                    {/* Add Participants Buttons */}
                    {activeView === "my-info" &&
                      formData.isKnownUser === "yes" &&
                      allParticipantsData.length === 0 &&
                      (formData.Category || categoryValue) !== "Single" && (
                        <div
                          onClick={handleAddParticipantTab}
                          className="flex items-center gap-2 border border-secondary px-4 py-2 my-4 rounded-md cursor-pointer w-full max-w-fit m-auto sm:w-auto justify-center sm:justify-start"
                        >
                          <IoIosAddCircleOutline size={24} />
                          <p className="text-secondary text-base font-medium">
                            Add Participant
                          </p>
                        </div>
                      )}
                  </div>
                </div>

                {participantTabs &&
                  allParticipantsData &&
                  participantTabs.map((tabId, index) => {
                    return (
                      <div
                        key={index}
                        className={activeView === tabId ? "block" : "hidden"}
                      >
                        <Participants
                          participantData={allParticipantsData[index]}
                          participantError={participantErrors?.[index]}
                          setParticipantErrors={setParticipantErrors}
                          index={index}
                          key={index}
                          tabId={tabId} // 👈 Pass tab ID like 'participant-1
                          handleAddParticipantTab={handleAddParticipantTab}
                          handleRemoveParticipantTab={
                            handleRemoveParticipantTab
                          }
                          activeView={activeView}
                          formErrors={formErrors}
                          onParticipantsChange={handleParticipantChange}
                          participantOutboundFlights={
                            participantOutboundFlights
                          }
                          participantInboundFlights={participantInboundFlights}
                          setParticipantOutboundFlights={
                            setParticipantOutboundFlights
                          }
                          setParticipantInboundFlights={
                            setParticipantInboundFlights
                          }
                          setFormErrors={setFormErrors}
                          setAllParticipantsData={setAllParticipantsData}
                          autoFillOptions={autoFillOptions}
                          setInboundFlights={setInboundFlights}
                          setOutboundFlights={setOutboundFlights}
                          fieldRefs={participantFieldRefs}
                          setEditedField={setEditedField}
                          setEditedScope={setEditedScope}
                        />
                      </div>
                    );
                  })}

                {showChoices && (
                  <Choices
                    showPopup={showPopup}
                    handleChange={handleChange}
                    formErrors={formErrors}
                    formData={formData}
                    onOptionChange={(opts) => {
                      setFormData((prev) => ({
                        ...prev,
                        tourOptions: opts, // ✅ set only selected ones here
                      }));
                    }}
                    onChoiceChange={(selected) => {
                      setFormData((prev) => ({
                        ...prev,
                        travelerChoice: selected,
                      }));
                    }}
                    setFormErrors={setFormErrors}
                    allParticipantsData={allParticipantsData}
                    isInvited={isInvited}
                  />
                )}

                {/* Terms and Conditions */}
                {showChoices && (
                  <div className="px-5 py-4 gap-1">
                    <div className="pb-4">
                      <Checkbox
                        name="insuranceConsent"
                        checked={formData.insuranceConsent}
                        onChange={(e) => {
                          const isChecked = e.target.checked;

                          handleChange({
                            target: {
                              name: "insuranceConsent",
                              value: isChecked,
                            },
                          });
                        }}
                        label={
                          <>
                            I consent that I am responsible for purchasing my
                            travel insurance, and I accept full responsibility
                            for any related expenses. I release{" "}
                            <span className="font-semibold">
                              BiblicalTouring.com
                            </span>{" "}
                            from any liability.
                          </>
                        }
                        items="items-start"
                        marginTop="mt-1"
                        required
                      />

                      {insuranceConsentError && (
                        <div className="flex gap-1 w-fit text-[10px] bg-red-100 text-red-600 mt-1 rounded p-[3px]">
                          <img src={Info} alt="info" />
                          {insuranceConsentError}
                        </div>
                      )}
                    </div>

                    <Checkbox
                      name="terms"
                      checked={formData.termsConditions}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        handleChange({
                          target: {
                            name: "termsConditions",
                            value: isChecked,
                          },
                        });
                      }}
                      label={
                        <>
                          I have read and agree to the tour{" "}
                          <Link
                            to="https://biblicaltouring.com/terms-condition/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <button
                              type="button"
                              className="text-buttonColor font-medium hover:underline"
                            >
                              Terms and Conditions
                            </button>
                          </Link>
                        </>
                      }
                      required
                    />
                    {agreeError && (
                      <div className="flex gap-1 w-fit text-[10px] bg-red-100 text-red-600 mt-1 rounded p-[3px]">
                        <img src={Info} alt="info" />
                        {agreeError}
                      </div>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <div
                  className={`px-4 py-3 border-b flex flex-col sm:flex-row items-center gap-3 sm:gap-0 ${
                    showSubmit &&
                    (allParticipantsData.length === 0 ||
                      activeIndex === allParticipantsData.length)
                      ? allParticipantsData.length === 0
                        ? "sm:justify-end" // only submit/update button
                        : "sm:justify-center" // both Add + Submit
                      : "sm:justify-center"
                  }`}
                >
                  {/* Submit Button */}
                  {/* {((activeIndex === -1 && formData.isKnownUser !== "yes") ||
                    activeIndex === allParticipantsData.length) && (
                    <div className="w-full sm:w-auto">
                      <Button
                        disabled={!formData.termsConditions}
                        className={`w-full sm:w-36 h-12 text-white ${
                          formData.termsConditions
                            ? "bg-green-600"
                            : "bg-buttonColor"
                        }`}
                        onClick={() => setSubmitClicked(true)}
                        text={!isNewUser ? "UPDATE" : "SUBMIT"}
                      />
                    </div>
                  )} */}

                  {
                    // --- NEW USER: show only in my-info or last participant
                    ((isNewUser &&
                      ((activeIndex === -1 &&
                        formData.isKnownUser !== "yes" &&
                        formData.Category !== "Double") ||
                        activeIndex === allParticipantsData.length)) ||
                      // --- NEW SCENARIO: Known user but no participants
                      formData.Category === "Single" ||
                      formData.isKnownUser === "no" ||
                      (formData.isKnownUser === "yes" &&
                        allParticipantsData.length !== 0 &&
                        // --- UPDATE USER: show always
                        !isNewUser)) && (
                      <div className="w-full sm:w-auto">
                        <Button
                          disabled={!formData.termsConditions}
                          className={`w-full sm:w-36 h-12 text-white ${
                            formData.termsConditions
                              ? "bg-green-600"
                              : "bg-buttonColor"
                          }`}
                          // onClick={() => setSubmitClicked(true)}
                          onClick={() => {
                            if (
                              !isNewUser &&
                              allParticipantsData.length > 0 &&
                              activeView === "my-info" &&
                              !modifiedUpdateStatus
                            ) {
                              handleAddParticipantTab();
                              setIsUpdateCopyFlow(true); // enable update mode
                            } else {
                              setSubmitClicked(true);
                            }
                          }}
                          text={!isNewUser ? "UPDATE" : "SUBMIT"}
                        />
                      </div>
                    )
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TermsModal
        open={showTermsModal}
        onAgree={handleAgreeTerms}
        onClose={handleDeclineTerms}
      />

      <ParticipantAutoFillPopup
        open={participantAutoFill}
        options={autoFillOptions}
        setOptions={setAutoFillOptions}
        onConfirm={confirmAddParticipant}
        onCancel={() => {
          setModifiedUpdateStatus(true);
          setParticipantAutoFill(false);
        }}
        isEdit={isUpdateCopyFlow}
      />
    </div>
  );
}
